import { SignalBase } from "./SignalBase";
import { SignalConnection } from "./SignalConnection";
//
// Flambe - Rapid game development
// https://github.com/aduros/flambe/blob/master/LICENSE.txt

/**
 * An alias for Signal1 listeners.
 */
interface Listener1<A> {
  (arg1: A): void;
}

/**
 * A one-argument signal. See Signal0 and Signal2 for different arities.
 */
export class Signal1<A> extends SignalBase {
  /**
   * @param listener An optional listener to immediately connect to the signal.
   */
  constructor(listener?: Listener1<A>) {
    super(listener);
  }

  /**
   * Connects a listener to this signal.
   * @param prioritize True if this listener should fire before others.
   * @returns A SignalConnection, that can be disposed to remove the listener.
   */
  public connect(
    listener: Listener1<A>,
    prioritize: boolean = false
  ): SignalConnection {
    return this.connectImpl(listener, prioritize);
  }

  /**
   * Emit the signal, notifying each connected listener.
   */
  public emit(arg1: A) {
    if (this.dispatching()) {
      this.defer(() => {
        this.emitImpl(arg1);
      });
    } else {
      this.emitImpl(arg1);
    }
  }

  private emitImpl(arg1: A) {
    let head = this.willEmit();
    let p = head;
    while (p != null) {
      p._listener(arg1);
      if (!p.stayInList) {
        p.dispose();
      }
      p = p._next as SignalConnection;
    }
    this.didEmit(head);
  }
}
